import React, { useCallback, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';

/*
 * Las preguntas que el calendario hace a mitad de una acción, como promesas:
 *   const alcance = await preguntas.alcance('editar')   → 'esta' | 'siguientes' | 'todas' | null (cancelado)
 *   const avisar  = await preguntas.aviso('nuevo')      → true | false | null (cancelado)
 *   const ok      = await preguntas.confirmar({...})    → true | false
 * Así mover, estirar, guardar y borrar comparten el mismo orden de preguntas sin anidar estados.
 */

const TEXTOS_ALCANCE = {
  editar: 'Editar evento periódico',
  mover: 'Mover evento periódico',
  borrar: 'Eliminar evento periódico',
};
const TEXTOS_AVISO = {
  nuevo: ['¿Enviar invitaciones a los invitados?', 'Las personas de tu organización lo verán en su calendario; las de fuera recibirán un correo con la invitación.', 'Enviar', 'No enviar'],
  cambio: ['¿Avisar a los invitados del cambio?', 'Se enviará la actualización a los invitados del evento.', 'Enviar', 'No enviar'],
  borrar: ['¿Avisar a los invitados de la cancelación?', 'Se enviará un aviso de cancelación a los invitados.', 'Avisar', 'No avisar'],
};

export function usePreguntas() {
  const [estado, setEstado] = useState(null);
  const resolver = useRef(null);

  const preguntar = useCallback((datos) => new Promise((resolve) => {
    resolver.current = resolve;
    setEstado(datos);
  }), []);

  const responder = (valor) => {
    const r = resolver.current;
    resolver.current = null;
    setEstado(null);
    if (r) r(valor);
  };

  const alcance = useCallback((accion = 'editar') => preguntar({ tipo: 'alcance', accion }), [preguntar]);
  const aviso = useCallback((cual = 'cambio') => preguntar({ tipo: 'aviso', cual }), [preguntar]);
  const confirmar = useCallback((datos) => preguntar({ tipo: 'confirmar', ...datos }), [preguntar]);

  const nodo = <DialogoPregunta estado={estado} responder={responder} />;
  return { alcance, aviso, confirmar, nodo };
}

function DialogoPregunta({ estado, responder }) {
  const [opcion, setOpcion] = useState('esta');
  const cancelar = useRef(null);
  const abierto = !!estado;

  return (
    <Dialog open={abierto} onOpenChange={(v) => { if (!v) responder(estado?.tipo === 'confirmar' ? false : null); }}>
      {estado?.tipo === 'alcance' && (
        <DialogContent className="sm:max-w-[380px]" data-testid="cal-pregunta-alcance">
          <DialogHeader>
            <DialogTitle>{TEXTOS_ALCANCE[estado.accion] || TEXTOS_ALCANCE.editar}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-1" role="radiogroup">
            {[
              ['esta', 'Este evento'],
              ['siguientes', 'Este evento y los siguientes'],
              ['todas', 'Todos los eventos'],
            ].map(([v, t]) => (
              <label key={v} className="cal-radio">
                <input type="radio" name="alcance" value={v} checked={opcion === v} onChange={() => setOpcion(v)} />
                <span>{t}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => responder(null)}>Cancelar</Button>
            <Button onClick={() => { const v = opcion; setOpcion('esta'); responder(v); }} data-testid="cal-alcance-aceptar">Aceptar</Button>
          </DialogFooter>
        </DialogContent>
      )}
      {estado?.tipo === 'aviso' && (
        <DialogContent className="sm:max-w-[420px]" data-testid="cal-pregunta-aviso">
          <DialogHeader>
            <DialogTitle>{TEXTOS_AVISO[estado.cual][0]}</DialogTitle>
            <DialogDescription style={{ color: 'var(--text-2)' }}>{TEXTOS_AVISO[estado.cual][1]}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => responder(null)}>Volver</Button>
            <Button variant="outline" onClick={() => responder(false)} data-testid="cal-aviso-no">{TEXTOS_AVISO[estado.cual][3]}</Button>
            <Button onClick={() => responder(true)} data-testid="cal-aviso-si">{TEXTOS_AVISO[estado.cual][2]}</Button>
          </DialogFooter>
        </DialogContent>
      )}
      {estado?.tipo === 'confirmar' && (
        <DialogContent
          className="sm:max-w-[420px]"
          data-testid="cal-pregunta-confirmar"
          onOpenAutoFocus={(e) => { e.preventDefault(); cancelar.current?.focus(); }}
        >
          <DialogHeader>
            <DialogTitle>{estado.titulo}</DialogTitle>
            {estado.mensaje && <DialogDescription style={{ color: 'var(--text-2)' }}>{estado.mensaje}</DialogDescription>}
          </DialogHeader>
          <DialogFooter>
            <Button ref={cancelar} variant="outline" onClick={() => responder(false)}>Cancelar</Button>
            <Button
              style={estado.peligro === false ? undefined : { background: 'var(--danger)', color: '#fff' }}
              onClick={() => responder(true)}
              data-testid="cal-confirmar-si"
            >
              {estado.boton || 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      )}
    </Dialog>
  );
}
